import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next, scheduleOnce } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

interface Item {
	uniqueId: number | string;
	isDestroying?: boolean;
	isDestroyed?: boolean;
}

const enum UpdateAction {
	RESET = 'reset',
	BOTTOM = 'bottom',
}

interface Args<I extends Item> {
	allItems: I[];
	nextItemsShown?: number;
	bottomDistance?: number;
	onVisibleItemsChanged?: () => void;
}

interface CommonListAutoLoadedComponentInterface<I extends Item> {
	Args: Args<I>;
	Blocks: { default: [items: I[]] }; // this is needed for yield
}

// I: the items given (inferred from @allItems), yielded as they are
export default class CommonListAutoLoadedComponent<I extends Item> extends Component<
	CommonListAutoLoadedComponentInterface<I>
> {
	@tracked visibleItems: I[] = [];
	@tracked currentItemsShown = 0;

	initialItemsShown = 60;
	nextItemsShown = 5;
	bottom = 0;
	bottomDistance = 60;

	scrollableNode: HTMLElement | null = null;

	constructor(owner: Owner, args: Args<I>) {
		super(owner, args);

		/* start Options
			Can't be dynamically changed during lifecycle
			Numeric must be more than 0
		*/
		if (this.args.nextItemsShown) {
			this.nextItemsShown = this.args.nextItemsShown;
		}
		if (this.args.bottomDistance) {
			this.bottomDistance = this.args.bottomDistance;
		}
		/* end Options */

		this.updateVisibleItems(UpdateAction.RESET, true);
	}

	updateVisibleItems(action: UpdateAction, isInitialCheck = false) {
		if (action === UpdateAction.BOTTOM && this.args.allItems.length < this.currentItemsShown) {
			return;
		}

		// reset action can also occurs when custom query or calculated metric were edited from left sidebar in designer
		// in that case, we don't want to reset scroller or visible items

		if (
			action === UpdateAction.RESET &&
			!isInitialCheck &&
			this.visibleItems &&
			!this.visibleItems.find((item) => item.isDestroying) &&
			!this.visibleItems.find((item) => item.isDestroyed)
		) {
			const itemsIds = this.args.allItems.map((item) => item.uniqueId);
			const visibleItemsIds = this.visibleItems.map((item) => item.uniqueId);
			if (
				itemsIds.length > 0 &&
				visibleItemsIds.length > 0 &&
				visibleItemsIds.length === itemsIds.length &&
				visibleItemsIds.every((visibleItemId) => itemsIds.includes(visibleItemId))
			) {
				return;
			}
		}

		let newItemsShown =
			action === UpdateAction.RESET ? this.initialItemsShown : this.nextItemsShown;
		if (action === UpdateAction.BOTTOM) {
			newItemsShown += this.currentItemsShown;
		}

		this.currentItemsShown = newItemsShown;
		this.visibleItems = this.args.allItems.slice(0, this.currentItemsShown);
		scheduleOnce('afterRender', this, this.updateBottom);

		if (action === UpdateAction.RESET && this.scrollableNode) {
			this.scrollableNode.scrollTop = 0;
		}

		const onVisibleItemsChanged = this.args.onVisibleItemsChanged;
		if (onVisibleItemsChanged) {
			next(() => {
				scheduleOnce('afterRender', this, onVisibleItemsChanged);
			});
		}
	}

	updateBottomVisibleItems() {
		if (
			this.scrollableNode &&
			this.bottom - this.bottomDistance <= this.scrollableNode.scrollTop
		) {
			this.updateVisibleItems(UpdateAction.BOTTOM);
		}
	}

	updateBottom() {
		const node = this.scrollableNode;
		if (node) {
			this.bottom = node.scrollHeight - node.offsetHeight;
		}
	}

	@action
	onContainerResize() {
		this.updateBottom();
		this.debounceUpdateBottomVisibleItems();
	}

	@action
	debounceUpdateBottomVisibleItems() {
		debounce(this, this.updateBottomVisibleItems, 150);
	}

	@action
	onAllItemsChange() {
		// if called in run.once, then parent component's 'afterRender' is executed before
		// visibleItems are updated (this can lead to wrong 'add link' positioning)
		this.updateVisibleItems(UpdateAction.RESET);
	}

	@action
	setupView(node: HTMLElement) {
		const scrollableNode = node.parentElement;
		this.scrollableNode = scrollableNode;

		if (scrollableNode) {
			scrollableNode.addEventListener('scroll', this.debounceUpdateBottomVisibleItems);
			scrollableNode.addEventListener('resize', this.onContainerResize); // called from left panel when collapsing visualizations section
		}
	}

	@action
	teardownView() {
		this.scrollableNode?.removeEventListener('scroll', this.debounceUpdateBottomVisibleItems);
		this.scrollableNode?.removeEventListener('resize', this.onContainerResize);
	}
	<template>
		{{yield this.visibleItems}}

		<span
			class="hidden"
			{{didInsert this.setupView}}
			{{didUpdate this.onAllItemsChange @allItems}}
			{{willDestroy this.teardownView}}
		></span>
	</template>
}
